import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./payment-processor.module.scss";
import { Button } from "../../../components";
import useWindowDimensions, { CurrentScreen } from "../../../../../hooks/use-window-dimensions";
import { useLazyVerifyTransactionQuery } from "../../../../../service/charge-service";
import { toast } from "react-toastify";
import { LoadingIconV2 } from "../../../../../assets/icons";
import { useDispatch } from "react-redux";
import { setTransactionReference } from "../../../../../stores/slices/buy-electricity-slice";
import type { BillingFailureReason } from "../../../../../services/business-analytics/vendEvents";

// Payment processor states
type PaymentState =
	| "redirecting" // About to redirect to payment gateway
	| "validating" // Validating payment after callback
	| "vending" // Vending in progress (status 1)
	| "success" // Payment successful (status 2)
	| "failed"; // Payment failed (status 3 or max retries)

// Vending status enum matching backend
enum VendingStatus {
	UNKNOWN = 0,
	IN_PROGRESS = 1,
	COMPLETED = 2,
	FAILED = 3,
}

interface PaymentProcessorProps {
	// For card payments - initial redirect
	paymentUrl?: string;
	reference?: string;
	vendingDetailsId?: string;

	// For wallet payments - direct validation
	skipRedirect?: boolean;

	// Callbacks
	onSuccess?: (reference: string) => void;
	onFailed?: (reference: string, reason: BillingFailureReason) => void;
	onBack?: () => void;

	// Display text customization
	redirectDelay?: number; // milliseconds
}

export const PaymentProcessor: React.FC<PaymentProcessorProps> = ({
	paymentUrl,
	reference: initialReference,
	vendingDetailsId,
	skipRedirect = false,
	onSuccess,
	onFailed,
	onBack,
	redirectDelay = 3000,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const currentScreen: CurrentScreen = useWindowDimensions();
	const [searchParams] = useSearchParams();

	const [state, setState] = useState<PaymentState>(skipRedirect ? "validating" : "redirecting");
	const [countdown, setCountdown] = useState(Math.ceil(redirectDelay / 1000));
	const pollingAttemptsRef = useRef(0);
	const [reference, setReference] = useState<string | null>(initialReference || null);

	const [validatePayment] = useLazyVerifyTransactionQuery();
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const hasRedirected = useRef(false);

	const MAX_POLLING_ATTEMPTS = 5;
	const POLLING_INTERVAL = 2000; // 2 seconds

	// Get reference from URL params (after callback)
	useEffect(() => {
		const urlReference = searchParams.get("reference");
		if (urlReference && !reference) {
			setReference(urlReference);
			dispatch(setTransactionReference((urlReference || reference) ?? ""));
			setState("validating");
		}
	}, [searchParams, reference]);

	// Countdown timer for redirect
	useEffect(() => {
		if (state === 'redirecting' && countdown > 0) {
			const timer = setTimeout(() => {
				setCountdown(countdown - 1);
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [state, countdown]);

	// Auto-redirect for card payments
	useEffect(() => {
		if (state === 'redirecting' && paymentUrl && !hasRedirected.current) {
			const redirectTimer = setTimeout(() => {
				handleRedirect();
			}, redirectDelay);

			return () => clearTimeout(redirectTimer);
		}
	}, [state, paymentUrl, redirectDelay]);

	// Start payment validation
	useEffect(() => {
		if ((state === "validating" || state === "vending") && reference) {
			validatePaymentStatus();
		}
	}, [state, reference]);

	const handleRedirect = () => {
		if (paymentUrl && !hasRedirected.current) {
			hasRedirected.current = true;
			window.location.href = paymentUrl;
		}
	};

	const validatePaymentStatus = async () => {
		if (!reference) return;

		// Check if max polling attempts reached
		if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
			stopPolling();
			setState("failed");
			toast.error(t("buyElectricity.web.paymentVerificationTimeout"));
			if (onFailed) {
				onFailed(reference, "verification_timeout");
			}
			return;
		}

		// Increment polling attempts counter
		pollingAttemptsRef.current++;

		try {
			const response = await validatePayment({ reference }).unwrap();

			const status = response.status as VendingStatus;

			switch (status) {
				case VendingStatus.COMPLETED:
					// Payment successful
					stopPolling();
					setState("success");
					if (onSuccess) {
						onSuccess(response.reference);
					}
					break;

				case VendingStatus.FAILED:
					// Payment failed
					stopPolling();
					setState("failed");
					if (onFailed) {
						onFailed(reference, "payment_failed");
					}
					break;

				case VendingStatus.IN_PROGRESS:
					// Still processing - set state and start/continue polling
					if (state !== "vending") {
						setState("vending");
					}
					startPolling();
					break;

				case VendingStatus.UNKNOWN:
					// Unknown status - continue polling until max attempts
					startPolling();
					break;
			}
		} catch (error: any) {
			console.error("Payment validation error:", error);
			// On error, continue polling until max attempts reached
			startPolling();
		}
	};

	const startPolling = () => {
		// Clear any existing polling
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
		}

		// Start new polling interval
		pollingIntervalRef.current = setInterval(() => {
			validatePaymentStatus();
		}, POLLING_INTERVAL);
	};

	const stopPolling = () => {
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
		pollingAttemptsRef.current = 0;
	};

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, []);

	const handleManualRedirect = () => {
		handleRedirect();
	};

	const handleGoBack = () => {
		stopPolling();
		if (onBack) {
			onBack();
		} else {
			navigate(-1);
		}
	};

	// Render different states
	const renderContent = () => {
		switch (state) {
			case "redirecting":
				return (
					<>
						<h2 className={styles.title}>{t("buyElectricity.web.redirectingToPayment")}</h2>
						<p className={styles.subtitle}>{t("buyElectricity.web.redirectToProviderNote")}</p>
					</>
				);

			case "validating":
				return (
					<>
						<h2 className={styles.title}>{t("buyElectricity.web.validatingPayment")}</h2>
						<p className={styles.subtitle}>{t("buyElectricity.web.confirmingPaymentDetails")}</p>
					</>
				);

			case "vending":
				return (
					<>
						<h2 className={styles.title}>{t("common.labels.vendingInProgress")}</h2>
						<p className={styles.subtitle}>{t("payment.yourTokenIsOnItsWay")}</p>
					</>
				);

			case "success":
				return (
					<>
						<h2 className={styles.title}>{t("payment.paymentSuccessfull")}</h2>
						<p className={styles.subtitle}>{t("payment.yourPaymentHasBeenReceived")}</p>
					</>
				);

			case "failed":
				return (
					<>
						<h2 className={styles.title}>{t("payment.paymentFailed")}</h2>
						<p className={styles.subtitle}>{t("buyElectricity.web.unableToCompletePayment")}</p>
					</>
				);

			default:
				return null;
		}
	};

	return (
		<div className={styles.paymentProcessorOverlay}>
			<div className={styles.paymentProcessor}>
				<div className={styles.contentArea}>
					<div className={styles.iconContainer}>
						<div className={styles.loadingSpinner}>
							<LoadingIconV2 className={styles.spinner} />
						</div>
					</div>
					{renderContent()}
				</div>
			</div>
		</div>
	);
};

export default PaymentProcessor;
